import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, X, ExternalLink, Store, PackageX } from "lucide-react";
import api, { API } from "../lib/apiClient";
import { useAuth } from "./AuthContext";

const DealLinkContext = createContext(null);
export const useDealLink = () => useContext(DealLinkContext);

// Resolve where to actually send the shopper once the deal is confirmed available.
function resolveOpenUrl(deal) {
  const raw = deal.openUrl || deal.open_url;
  if (raw) return raw.startsWith("http") ? raw : `${API}${raw}`;
  return deal.checkUrl || deal.url;
}

export function DealLinkProvider({ children }) {
  const [state, setState] = useState({ open: false, phase: "idle", similar: [] });
  const { user } = useAuth();
  const navigate = useNavigate();

  const openDeal = useCallback(async (deal) => {
    // Paywall: only members with full access (paid / trial / comp / admin) may reveal purchase links.
    const premium = user && !!user.is_premium;
    if (!user) { navigate("/auth"); return; }
    if (!premium) { navigate("/upgrade"); return; }

    const checkUrl = deal.checkUrl || deal.url;
    // Reserve a tab synchronously (inside the user gesture) so no popup blocker interferes.
    const tab = window.open("", "_blank");
    setState({ open: true, phase: "checking", similar: [] });
    let res;
    try {
      const { data } = await api.post("/deals/check", {
        url: checkUrl,
        id: deal.id || "",
        department: deal.department || "",
      });
      res = data;
    } catch (err) {
      if (err.response?.status === 403) { // server-side paywall guard
        if (tab) tab.close();
        setState({ open: false, phase: "idle", similar: [] });
        navigate("/upgrade");
        return;
      }
      res = { available: true, status: "unknown" }; // never block the shopper on our own error
    }

    if (res.available) {
      const dest = resolveOpenUrl(deal);
      if (tab) tab.location.href = dest;
      else window.open(dest, "_blank");
      setState({ open: false, phase: "idle", similar: [] });
    } else {
      if (tab) tab.close();
      setState({ open: true, phase: "unavailable", similar: res.similar || [] });
    }
  }, [user, navigate]);

  const close = () => setState({ open: false, phase: "idle", similar: [] });

  const ctx = useMemo(() => ({ openDeal }), [openDeal]);

  return (
    <DealLinkContext.Provider value={ctx}>
      {children}
      {state.open && (
        <div
          data-testid="deal-check-modal"
          className="fixed inset-0 z-[100] bg-ink/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={state.phase === "unavailable" ? close : undefined}
        >
          <div
            className="bg-white rounded-3xl shadow-lift w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 md:p-8 fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            {state.phase === "checking" ? (
              <div className="text-center py-8" data-testid="deal-check-loading">
                <Loader2 className="animate-spin text-brand mx-auto" size={40} />
                <p className="font-heading font-extrabold text-xl text-ink mt-5">
                  One second, checking this deal is still available for you…
                </p>
                <p className="text-ink-soft mt-1">Hang tight, this only takes a moment.</p>
              </div>
            ) : (
              <div data-testid="deal-unavailable">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-blush text-brand flex items-center justify-center shrink-0">
                      <PackageX size={24} />
                    </div>
                    <div>
                      <h3 className="font-heading font-black text-xl text-ink leading-tight">
                        Sorry, this deal is no longer available.
                      </h3>
                      <p className="text-ink-soft text-sm mt-0.5">Here are some similar deals you might like.</p>
                    </div>
                  </div>
                  <button onClick={close} data-testid="deal-check-close" className="text-ink-muted hover:text-ink p-1">
                    <X size={22} />
                  </button>
                </div>

                {state.similar.length > 0 ? (
                  <div className="grid sm:grid-cols-2 gap-4 mt-6" data-testid="similar-deals">
                    {state.similar.map((d) => (
                      <SimilarCard key={d.id} deal={d} onOpen={openDeal} />
                    ))}
                  </div>
                ) : (
                  <p className="text-ink-soft mt-6 text-center py-6">
                    New deals are being added daily — check back soon.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </DealLinkContext.Provider>
  );
}

function SimilarCard({ deal, onOpen }) {
  return (
    <button
      onClick={() => onOpen(deal)}
      data-testid={`similar-deal-${deal.id}`}
      className="group text-left bg-white rounded-2xl border border-line overflow-hidden hover:shadow-lift hover:-translate-y-0.5 transition-all flex"
    >
      <div className="w-24 h-24 bg-blush shrink-0 overflow-hidden">
        <img src={deal.image} alt={deal.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
      </div>
      <div className="p-3 flex-1 min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-wide text-brand flex items-center gap-1 truncate">
          <Store size={10} /> {deal.store}
        </p>
        <p className="font-heading font-bold text-sm text-ink leading-snug line-clamp-2">{deal.title}</p>
        <p className="text-sm text-ink-soft mt-1 flex items-center gap-1">
          {deal.price > 0 ? `$${Number(deal.price).toFixed(2)}` : "See deal"} <ExternalLink size={12} />
        </p>
      </div>
    </button>
  );
}
